'use client';

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import { BookOpen, HelpCircle, X } from 'lucide-react';
import type { ScreenHintSet } from '@/lib/help/screen-hints';
import { cn } from '@/lib/utils';

interface Position {
  id: string;
  target: DOMRect;
  top: number;
  left: number;
  width: number;
}

/** 아직 그리지 않은 메모지의 높이 어림. 그린 뒤에는 실제 높이를 쓴다. */
const CARD_HEIGHT_ESTIMATE = 152;
/** 어둡게 덮을 때 설명하는 칸 둘레로 남기는 여백. 테두리(ring-2)와 그 간격(ring-offset-2)이 들어간다. */
const SPOT_PAD = 4;

/** 원래 화면의 배치와 조작을 유지하는, 선택해서 여는 안내 레이어. */
export function ScreenHelp({ set, onClose }: { set: ScreenHintSet; onClose: () => void }) {
  const [positions, setPositions] = useState<Position[]>([]);
  const cards = useRef(new Map<string, HTMLDivElement>());
  const maskId = `screen-help-spot-${useId().replace(/:/g, '')}`;

  const place = useCallback(() => {
    const width = Math.min(216, window.innerWidth - 24);
    const bottomReserve = window.innerWidth < 640 ? 68 : 12;
    // 머리 띠 안의 고정점(작성 화면)은 띠 아래로 숨지 않으므로 거르지 않는다.
    const anchorTop = set.headerAnchors ? 0 : 72;
    const visible = set.hints.flatMap((hint) => {
      const targets = document.querySelectorAll(hint.selector ?? `[data-help-anchor="${hint.id}"]`);
      for (const target of targets) {
        const rect = target.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0 && rect.bottom >= anchorTop && rect.top <= window.innerHeight - 24) {
          return [{ id: hint.id, target: rect }];
        }
      }
      return [];
    }).sort((a, b) => a.target.top - b.target.top);

    let bottom = 72;
    const next: Position[] = [];
    for (const { id, target } of visible) {
      const height = cards.current.get(id)?.offsetHeight || CARD_HEIGHT_ESTIMATE;
      // 좁은 화면에는 옆자리가 없다. 목표가 짧으면 그 아래에 붙여 원래 단추를 가리지 않는다.
      // 머리 띠 안의 고정점은 옆에 두면 띠의 다른 단추와 「화면 도움말」 줄을 가리므로 늘 바로 아래에 둔다.
      const belowTarget = set.headerAnchors
        || (window.innerWidth < 640 && target.bottom + CARD_HEIGHT_ESTIMATE + bottomReserve + 12 <= window.innerHeight);
      const preferred = Math.max(72, (belowTarget ? target.bottom : target.top) + 12);
      const preferredLeft = set.headerAnchors
        ? target.left
        : target.right + width + 24 <= window.innerWidth
        ? target.right + 12
        : target.left >= width + 24
          ? target.left - width - 12
          : target.left + 12;
      const left = Math.max(12, Math.min(preferredLeft, window.innerWidth - width - 12));
      // 머리 띠 메모지는 가로로 나란히 선다. 앞 메모지와 가로로 겹칠 때만 그 아래로 내린다.
      const top = set.headerAnchors
        ? next.reduce((y, card) => (card.left < left + width + 8 && left < card.left + card.width + 8
          ? Math.max(y, card.top + (cards.current.get(card.id)?.offsetHeight || CARD_HEIGHT_ESTIMATE) + 8) : y), preferred)
        : Math.max(preferred, bottom + 8);
      // 좁은 화면에서 뒤 메모지 때문에 앞 메모지를 탭 위로 밀지 않는다.
      // 아래 메모지는 그 요소를 스크롤해 올리면 나타난다.
      if (top + height > window.innerHeight - bottomReserve) continue;
      bottom = Math.max(bottom, top + height);
      next.push({ id, target, top, left, width });
    }
    setPositions((current) => {
      const same = current.length === next.length && current.every((item, i) => {
        const other = next[i];
        return item.id === other.id && item.top === other.top && item.left === other.left
          && item.width === other.width && item.target.x === other.target.x
          && item.target.y === other.target.y && item.target.width === other.target.width
          && item.target.height === other.target.height;
      });
      return same ? current : next;
    });
  }, [set.hints, set.headerAnchors]);

  useEffect(() => {
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(place);
    };
    schedule();
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    const observer = new ResizeObserver(schedule);
    for (const hint of set.hints) {
      for (const target of document.querySelectorAll(hint.selector ?? `[data-help-anchor="${hint.id}"]`)) {
        observer.observe(target);
      }
    }
    for (const card of cards.current.values()) observer.observe(card);
    // 탭 본문이 비동기로 도착해도 현재 화면의 고정점을 다시 찾는다.
    const main = document.querySelector('main');
    const mutations = new MutationObserver(schedule);
    if (main) mutations.observe(main, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      observer.disconnect();
      mutations.disconnect();
    };
  }, [place, set.hints, positions.length]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  // 층은 확인 창(z-50)과 같게 둔다. 같은 층이면 나중에 열린 확인 창이 위에 와서, 말풍선이 「최종 제출할까요?」를
  // 가리지 않는다(10-31 시각 시험). 휴대폰 머리줄(z-50)은 본문 앞쪽이라 말풍선이 여전히 그 위에 그려진다.
  return createPortal(
    <div aria-label="화면 도움말" className="pointer-events-none fixed inset-0 z-50">
      {/* 설명하는 칸만 밝게 두고 나머지는 살짝 어둡게(사용자 10-06). 조작은 그대로 되도록 누름은 통과시킨다. */}
      {positions.length > 0 && (
        <svg aria-hidden="true" className="fixed inset-0 h-full w-full">
          <defs>
            <mask id={maskId}>
              <rect width="100%" height="100%" fill="white" />
              {positions.map(({ id, target }) => (
                <rect key={id} x={target.left - SPOT_PAD} y={target.top - SPOT_PAD}
                  width={target.width + SPOT_PAD * 2} height={target.height + SPOT_PAD * 2} rx={10} fill="black" />
              ))}
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="black" fillOpacity={0.18} mask={`url(#${maskId})`} />
        </svg>
      )}
      {positions.map(({ id, target }, index) => (
        <div key={`outline-${id}`} className="fixed rounded-lg ring-2 ring-warning ring-offset-2 ring-offset-background" style={{ left: target.left, top: target.top, width: target.width, height: target.height }}>
          <span className="absolute -left-2 -top-2 grid size-5 place-items-center border border-warning-subtle-border bg-warning-subtle text-[11px] font-semibold text-warning-subtle-foreground shadow-sm">{index + 1}</span>
        </div>
      ))}
      {positions.map(({ id, top, left, width }, index) => {
        const hint = set.hints.find((item) => item.id === id)!;
        return (
          <div
            key={id}
            data-help-card={id}
            ref={(node) => { if (node) cards.current.set(id, node); else cards.current.delete(id); }}
            className={cn(
              'pointer-events-auto fixed border border-warning-subtle-border bg-warning-subtle p-3 text-sm text-warning-subtle-foreground',
              hint.attention && 'border-l-4 border-l-warning',
            )}
            style={{
              top,
              left,
              width,
              boxShadow: '4px 4px 0 hsl(var(--warning-subtle-border) / 0.55), 0 10px 20px -12px hsl(var(--warning) / 0.25)',
            }}
          >
            <div className="mb-1 flex items-center gap-2 text-balance font-semibold"><span className="grid size-5 shrink-0 place-items-center border border-warning-subtle-border bg-background/70 text-xs text-warning-subtle-foreground">{index + 1}</span>{hint.title}</div>
            <p className="text-pretty text-[13px] leading-5 text-warning-subtle-foreground">{hint.short}</p>
            {/* 짧은 안내라 접지 않고 다 펼친다. 자세한 문서는 위 「문서 보기」 하나로(사용자 10-06). */}
            <p className="mt-1 text-pretty text-[13px] leading-5 text-warning-subtle-foreground">{hint.detail}</p>
          </div>
        );
      })}
      {/* 안내를 닫는 자리. 어둡게 덮는 층보다 뒤에 그려 이 줄은 어두워지지 않는다. 눈에 띄게 진한 테두리, 닫기는 빨간 네모(사용자 10-06). */}
      <div className="pointer-events-auto fixed right-[max(0.75rem,env(safe-area-inset-right))] top-[max(0.75rem,env(safe-area-inset-top))] flex items-center gap-1.5 rounded-lg border-2 border-primary bg-background p-1.5 pl-3 text-sm font-semibold text-foreground shadow-lg max-sm:bottom-[max(0.75rem,env(safe-area-inset-bottom))] max-sm:top-auto">
        <HelpCircle className="size-4 shrink-0 text-primary" aria-hidden="true" />
        <span className="max-sm:sr-only">화면 도움말</span>
        <Link href={`/help/${set.docId}`} target="_blank" rel="noreferrer" aria-label="문서 보기" className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 font-medium text-primary hover:bg-primary-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <BookOpen className="size-4" aria-hidden="true" /> <span className="max-sm:sr-only">문서 보기</span>
        </Link>
        <button type="button" onClick={onClose} aria-label="화면 도움말 닫기" title="화면 도움말 닫기" className="grid size-9 place-items-center rounded-md border-2 border-destructive text-destructive hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <X className="size-5" strokeWidth={2.75} aria-hidden="true" />
        </button>
      </div>
    </div>,
    document.body,
  );
}
